import h from 'hyperscript';

import { logger } from '@disy/lib/logging';

import { hasPrivilege } from 'cadenza/access-management/model/object-privilege-util';
import { errorAlert } from 'cadenza/alert';
import { getAllCuratedMapViews } from 'cadenza/api-client/curated-map-views-api';
import type { CuratedMapViewDto } from 'cadenza/api-client/model/curated-map-view-dto';
import type {
  AddNewViewWithDataViewCommandPayload,
  AddViewCommandPayload,
} from 'cadenza/api-client/workbook-command-payloads';
import { Command } from 'cadenza/api-client/workbook-commands';
import { getStore } from 'cadenza/store/cadenza-store';
import type { VisualizationTypeLabel } from 'cadenza/ui/visualization-type-label/visualization-type-label';
import { DataViewVisualizationKey, getViewType } from 'cadenza/workbook/api/data-view-visualization-key';
import { ViewType } from 'cadenza/workbook/api/view-type';
import { getBaseMapViewConfigOrSelectInDialog } from 'cadenza/workbook/default-map-utils/default-map-utils';
import { addEmptyMapView } from 'cadenza/workbook/empty-map-view-creation/add-empty-map-view';
import { isAnyDefaultMapViewAvailable } from 'cadenza/workbook/state/default-map.state';
import { executeCommand } from 'cadenza/workbook/workbook-core-reducers.state';
import { getActiveWorksheetId } from 'cadenza/workbook/workbook-core-selectors.state';
import { minimizeView } from 'cadenza/workbook/workbook.state';

import i18n from './workbook-add-new-view.properties';

export async function openVisualizationPickerModalAndAddView() {
  try {
    const { VisualizationPickerModal } =
      await import('cadenza/workbook/visualisation-picker/visualization-picker-modal/visualization-picker-modal');
    const visualization = await new VisualizationPickerModal().connectAndShow();
    if (visualization === 'MAP') {
      await addView(ViewType.MAP);
      getStore().dispatch(minimizeView());
    } else if (visualization === 'EMPTY_MAP') {
      await addEmptyMapView();
      getStore().dispatch(minimizeView());
    } else if (visualization === 'MAP_VIEW_FROM_REPOSITORY') {
      const curatedMapViews = await getCuratedMapViews();
      const newMapView = await openLoadCuratedMapViewDialog(curatedMapViews);
      if (newMapView) {
        getStore().dispatch(minimizeView());
      }
    } else if (visualization) {
      await addViewWithDataView(visualization.key);
      getStore().dispatch(minimizeView());
    }
  } catch (err) {
    logger.error('Error when trying to create view ', err);
    errorAlert(i18n('error'));
  }
}

/**
 * Create the actions for the add dataView menu. These actions are also used in empty-view.js.
 * This must be a function and cannot be a constant because we need new icons
 * otherwise the icon elements will be moved from one menu to the other when it is rendered.
 *
 * @return Menu items for the add dataView menu
 */
export function createAddDataViewActions() {
  return [
    {
      label: h<VisualizationTypeLabel>('d-visualization-type-label', { type: 'TABLE.STRUCTURED' }, i18n('table')),
      execute: () =>
        addViewWithDataView(DataViewVisualizationKey.TABLE.STRUCTURED).then(() => getStore().dispatch(minimizeView())),
    },
    ...(isAnyDefaultMapViewAvailable(getStore().getState()) ?
      [
        {
          label: h<VisualizationTypeLabel>('d-visualization-type-label', { type: 'MAP' }, i18n('map')),
          execute: () => addView(ViewType.MAP).then(() => getStore().dispatch(minimizeView())),
        },
      ]
    : []),
    {
      label: h<VisualizationTypeLabel>(
        'd-visualization-type-label',
        { type: 'CHART.COLUMN_CHART' },
        i18n('columnChart'),
      ),
      execute: () =>
        addViewWithDataView(DataViewVisualizationKey.CHART.COLUMN_CHART).then(() =>
          getStore().dispatch(minimizeView()),
        ),
    },
    {
      label: h<VisualizationTypeLabel>('d-visualization-type-label', { type: 'VISUALIZATION_TYPE' }, i18n('other')),
      execute: () => openVisualizationPickerModalAndAddView(),
    },
  ];
}

async function addViewWithDataView(visualizationKey: DataViewVisualizationKey) {
  const worksheetId = getActiveWorksheetId(getStore().getState());
  const commandPayload: AddNewViewWithDataViewCommandPayload = { worksheetId, visualizationKey };
  if (getViewType(visualizationKey) === 'MAP') {
    const { baseMapViewId, canceled } = await getBaseMapViewConfigOrSelectInDialog();
    if (canceled) {
      return;
    }
    commandPayload.baseMapViewId = baseMapViewId;
  }
  getStore().dispatch(executeCommand(Command.ADD_NEW_VIEW_WITH_DATA_VIEW(commandPayload)));
}

async function addView(viewType: ViewType) {
  const worksheetId = getActiveWorksheetId(getStore().getState());
  const commandPayload: AddViewCommandPayload = { worksheetId, viewType };
  if (viewType === 'MAP') {
    const { baseMapViewId, canceled } = await getBaseMapViewConfigOrSelectInDialog();
    if (canceled) {
      return;
    }
    commandPayload.baseMapViewId = baseMapViewId;
  }
  getStore().dispatch(executeCommand(Command.ADD_VIEW(commandPayload)));
}

async function getCuratedMapViews() {
  let curatedMapViews = (await getAllCuratedMapViews()).rows;
  return curatedMapViews.filter((mapView) => hasPrivilege(mapView.currentUserPrivileges, 'visible'));
}

async function openLoadCuratedMapViewDialog(curatedMapViews: CuratedMapViewDto[]) {
  const { LoadCuratedMapViewDialog } =
    await import('cadenza/workbook/curated-map-views/load-curated-map-view-dialog/load-curated-map-view-dialog');
  return new LoadCuratedMapViewDialog(curatedMapViews).connectAndShow();
}
